import React, { FC, useEffect } from 'react';

import { NativeResources, Resources } from '@ariksa/inventory-core';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { isEmpty, map } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  DetailsIdentifier,
  formatNumberWithComma,
  renderRiskContextIcon,
  WithResourceIcon,
} from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { renderScanOnPush } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/utils';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { renderOSNameAndIcon } from 'containers/Visibility/Vulnerabilities/Components/hooks/utils';
import { renderResourceState } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';

interface ResourceDetailsProps {
  uuid: string;
}

export const ResourceDetails: FC<ResourceDetailsProps> = props => {
  const { uuid } = props;
  const {
    resourceInsight,
    resourceDetails,
    resourceVulnerabilityCount,
    resourceVulnerabilitySummary,
  } = useSelector(selectVulnerabilities);
  const { data: insight } = resourceInsight;
  const { data: details } = resourceDetails;
  const { data: summary } = resourceVulnerabilitySummary;
  const { renderAccountWithVPCAndRegion } = useCloudAccountId();
  const { riskContext } = useSelector(selectSharedState);
  //const params = useParams<{ id: string }>();
  const [patchesAvailable, setPatchesAvailable] = React.useState(0);

  useEffect(() => {
    setPatchesAvailable(
      (summary?.applications?.available_patches ?? 0) +
        (summary?.os_vulnerability?.available_patches ?? 0) +
        (summary?.library_vulnerability?.available_patches ?? 0),
    );
  }, [summary]);

  return (
    <Stack spacing={4} w="full">
      <HStack>
        <Stack spacing={4} w="full">
          {(insight?.agnostic_name === Resources.Containers ||
            insight?.agnostic_name === Resources.VirtualMachine) && (
            <DetailsIdentifier label="Cloud ID" value={insight?.resource_id} />
          )}

          <DetailsIdentifier
            label={
              insight?.agnostic_name === Resources.Containers ||
              insight?.agnostic_name === Resources.VirtualMachine
                ? 'Deployed In'
                : 'Account'
            }
            value={renderAccountWithVPCAndRegion(insight)}
          />

          {insight?.native_name === NativeResources.AmazonMachineImage ||
          insight?.agnostic_name === Resources.ServerlessCompute ? (
            <DetailsIdentifier label="Cloud ID" value={insight?.resource_id} />
          ) : insight?.native_name ===
            NativeResources.ElasticContainerRegistry ? (
            <DetailsIdentifier
              label="Registry"
              value={insight.repository_uri}
            />
          ) : (
            <DetailsIdentifier
              label="State"
              value={renderResourceState(insight)}
            />
          )}

          <DetailsIdentifier
            label="Vulnerabilities"
            value={renderVulnerabilities({
              value: resourceVulnerabilityCount.data?.vulnerabilities,
              spacing: 4,
              message: 'None',
            })}
            isLoading={resourceVulnerabilityCount.isLoading}
          />
        </Stack>
        <Stack spacing={4} w="full">
          {insight?.native_name === NativeResources.AmazonMachineImage ||
          insight?.native_name === NativeResources.ElasticContainerRegistry ? (
            <DetailsIdentifier
              label="Used By"
              /*value={
                formatNumberWithComma(insight?.identity ?? 0) +
                ' virtual machine' +
                (insight?.identity?.length === 1 ? '' : 's')
              }*/
            />
          ) : insight?.agnostic_name === Resources.ServerlessCompute ? (
            <DetailsIdentifier label="Role" value={details?.config?.Role} />
          ) : (
            <DetailsIdentifier label="Image" value={insight?.image} />
          )}

          {insight?.native_name === NativeResources.ElasticContainerRegistry ? (
            <>
              <DetailsIdentifier
                label="Scan On Push"
                value={renderScanOnPush(
                  details?.config?.imageScanningConfiguration?.scanOnPush,
                )}
              />
              <DetailsIdentifier label="Last Scanned" value={'-'} />
            </>
          ) : (
            <>
              {insight?.agnostic_name === Resources.ServerlessCompute ? (
                <DetailsIdentifier
                  label="Runtime"
                  value={details?.config?.Runtime}
                />
              ) : (
                <DetailsIdentifier
                  label="Operating System"
                  value={renderOSNameAndIcon(resourceVulnerabilityCount.data)}
                />
              )}
              {insight?.native_name === NativeResources.AmazonMachineImage ||
              insight?.agnostic_name === Resources.ServerlessCompute ? (
                <DetailsIdentifier
                  label="Public"
                  value={
                    <WithResourceIcon
                      resourceType={IconTypes.Globe}
                      iconFilled={false}
                      color={
                        insight?.public
                          ? customTheme.colors.critical
                          : customTheme.colors.gray['250']
                      }
                      useCustomColor
                      iconSize="xs"
                      spacing={1}
                    >
                      {insight?.public ? 'Yes' : 'No'}
                    </WithResourceIcon>
                  }
                />
              ) : (
                <>
                  <DetailsIdentifier label="Kubernetes" value={insight?.eks} />
                  <DetailsIdentifier
                    label="Risk Context"
                    value={
                      isEmpty(riskContext.data) ? (
                        'None'
                      ) : (
                        <HStack spacing={0.5}>
                          {map(riskContext.data?.[uuid], (o, index) => (
                            <Box key={index + '-risk-context'}>
                              {renderRiskContextIcon(o)}
                            </Box>
                          ))}
                        </HStack>
                      )
                    }
                    isLoading={riskContext.isLoading}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </HStack>
      {insight?.native_name === NativeResources.ElasticContainerRegistry && (
        <DetailsIdentifier label="Cloud ID" value={insight?.resource_id} />
      )}
    </Stack>
  );
};
